import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { api, formatApiErrorDetail } from "../lib/api";
import Navbar from "../components/Navbar";
import VerifyEmailBanner from "../components/VerifyEmailBanner";
import DangerZone from "../components/DangerZone";
import GrammarlyTip from "../components/GrammarlyTip";
import JobSearchHub from "../components/JobSearchHub";
import { Button } from "../components/ui/button";
import { Card } from "../components/ui/card";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogTrigger, DialogDescription,
} from "../components/ui/dialog";
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger,
} from "../components/ui/dropdown-menu";
import { Plus, FileText, MoreVertical, Copy, Trash2, Pencil, Loader2, Lock, Mail } from "lucide-react";
import { toast } from "sonner";

const TEMPLATES = [
  { id: "centered", name: "Default" },
  { id: "modern", name: "Modern" },
  { id: "classic", name: "Traditional" },
];

const TEMPLATE_LABELS = {
  centered: "Default",
  modern: "Modern",
  classic: "Traditional",
  minimal: "Minimal",
  creative: "Creative",
  professional: "Professional",
};

function formatDate(iso) {
  if (!iso) return "";
  try {
    return new Date(iso).toLocaleDateString(undefined, { month: "short", day: "numeric", year: "numeric" });
  } catch { return iso; }
}

export default function Dashboard() {
  const [resumes, setResumes] = useState(null);
  const [creating, setCreating] = useState(false);
  const [newTitle, setNewTitle] = useState("");
  const [newTemplate, setNewTemplate] = useState("centered");
  const [openCreate, setOpenCreate] = useState(false);
  const [coverLetters, setCoverLetters] = useState(null);
  const [openCreateCL, setOpenCreateCL] = useState(false);
  const [newCLTitle, setNewCLTitle] = useState("");
  const [newCLSource, setNewCLSource] = useState("");
  const [creatingCL, setCreatingCL] = useState(false);
  const nav = useNavigate();

  const load = async () => {
    try {
      const [{ data: r }, { data: cl }] = await Promise.all([
        api.get("/resumes"),
        api.get("/cover-letters"),
      ]);
      setResumes(r);
      setCoverLetters(cl);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message);
      setResumes([]);
      setCoverLetters([]);
    }
  };

  useEffect(() => { load(); }, []);

  const create = async () => {
    if (!newTitle.trim()) { toast.error("Please enter a title"); return; }
    setCreating(true);
    try {
      const { data } = await api.post("/resumes", {
        title: newTitle.trim(),
        template: newTemplate,
        accentColor: "#0f172a",
        showReferences: false,
        personalInfo: { fullName: "", headline: "", email: "", phone: "", location: "", website: "", linkedin: "" },
        summary: "",
        experience: [],
        education: [],
        skills: [],
        projects: [],
        references: [],
      });
      toast.success("Resume created");
      setOpenCreate(false);
      setNewTitle("");
      nav(`/builder/${data.id}`);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    } finally {
      setCreating(false);
    }
  };

  const duplicate = async (id) => {
    try {
      await api.post(`/resumes/${id}/duplicate`);
      toast.success("Duplicated");
      load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    }
  };

  const remove = async (id) => {
    if (!window.confirm("Delete this resume? This cannot be undone.")) return;
    try {
      await api.delete(`/resumes/${id}`);
      toast.success("Deleted");
      load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    }
  };

  const createCoverLetter = async () => {
    if (!newCLTitle.trim()) { toast.error("Please enter a title"); return; }
    setCreatingCL(true);
    try {
      let personalInfo = { fullName: "", email: "", phone: "", location: "" };
      if (newCLSource) {
        try {
          const { data: src } = await api.get(`/resumes/${newCLSource}`);
          const pi = src.personalInfo || {};
          personalInfo = {
            fullName: pi.fullName || "",
            email: pi.email || "",
            phone: pi.phone || "",
            location: pi.location || "",
          };
        } catch {
          // Non-fatal — proceed with empty personal info
        }
      }
      const { data } = await api.post("/cover-letters", {
        title: newCLTitle.trim(),
        sourceResumeId: newCLSource || null,
        accentColor: "#0f172a",
        personalInfo,
        recipient: {},
        role: "",
        answers: {},
        body: "",
      });
      toast.success("Cover letter created");
      setOpenCreateCL(false);
      setNewCLTitle("");
      setNewCLSource("");
      nav(`/cover-letter/${data.id}`);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    } finally {
      setCreatingCL(false);
    }
  };

  const removeCoverLetter = async (id) => {
    if (!window.confirm("Delete this cover letter? This cannot be undone.")) return;
    try {
      await api.delete(`/cover-letters/${id}`);
      toast.success("Deleted");
      load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    }
  };

  return (
    <div className="min-h-screen bg-slate-50">
      <Navbar />
      <main className="max-w-7xl mx-auto px-6 py-10">
        <VerifyEmailBanner />
        <GrammarlyTip />
        <JobSearchHub />
        <div className="flex items-end justify-between flex-wrap gap-4 mb-8">
          <div>
            <div className="flex items-center gap-2 text-xs font-mono-display text-emerald-700 bg-emerald-50 inline-flex px-2.5 py-1 rounded-md border border-emerald-100" data-testid="dashboard-encryption-badge">
              <Lock className="w-3 h-3" /> All resumes are encrypted at rest
            </div>
            <h1 className="mt-3 font-display text-3xl lg:text-4xl font-bold tracking-tight">Your resumes</h1>
            <p className="mt-1 text-slate-500">Edit, duplicate or download. Switch templates anytime.</p>
          </div>
          <Dialog open={openCreate} onOpenChange={setOpenCreate}>
            <DialogTrigger asChild>
              <Button className="bg-slate-900 hover:bg-slate-800 h-11 px-5" data-testid="dashboard-new-resume-btn">
                <Plus className="w-4 h-4 mr-2" /> New resume
              </Button>
            </DialogTrigger>
            <DialogContent>
              <DialogHeader>
                <DialogTitle>Create a new resume</DialogTitle>
                <DialogDescription>Give it a title and pick a starting template — you can switch later.</DialogDescription>
              </DialogHeader>
              <div className="space-y-4">
                <div>
                  <Label htmlFor="title">Title</Label>
                  <Input id="title" value={newTitle} onChange={(e) => setNewTitle(e.target.value)} placeholder="e.g. Senior Product Designer 2026" data-testid="new-resume-title-input" />
                </div>
                <div>
                  <Label>Template</Label>
                  <div className="grid grid-cols-3 gap-2 mt-1.5">
                    {TEMPLATES.map((t) => (
                      <button
                        key={t.id}
                        type="button"
                        onClick={() => setNewTemplate(t.id)}
                        className={`p-2 rounded-lg border text-xs font-medium transition ${newTemplate === t.id ? "border-slate-900 bg-slate-900 text-white" : "border-slate-200 hover:border-slate-400"}`}
                        data-testid={`new-template-${t.id}`}
                      >
                        {t.name}
                      </button>
                    ))}
                  </div>
                </div>
              </div>
              <DialogFooter>
                <Button variant="outline" onClick={() => setOpenCreate(false)}>Cancel</Button>
                <Button onClick={create} disabled={creating} className="bg-slate-900 hover:bg-slate-800" data-testid="new-resume-create-btn">
                  {creating ? <Loader2 className="w-4 h-4 animate-spin" /> : "Create resume"}
                </Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>
        </div>

        {resumes === null ? (
          <div className="flex items-center justify-center py-20">
            <Loader2 className="w-6 h-6 animate-spin text-slate-400" />
          </div>
        ) : resumes.length === 0 ? (
          <Card className="p-12 text-center border-dashed" data-testid="empty-state">
            <div className="w-12 h-12 rounded-xl bg-slate-100 text-slate-500 mx-auto flex items-center justify-center">
              <FileText className="w-6 h-6" />
            </div>
            <h3 className="mt-4 font-display text-lg font-semibold">No resumes yet</h3>
            <p className="text-sm text-slate-500 mt-1 max-w-sm mx-auto">Hit “New resume” to start. You&apos;ll be guided step by step.</p>
            <Button onClick={() => setOpenCreate(true)} className="mt-5 bg-slate-900 hover:bg-slate-800">
              <Plus className="w-4 h-4 mr-2" /> Create your first resume
            </Button>
          </Card>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5" data-testid="resume-grid">
            {resumes.map((r) => (
              <Card key={r.id} className="p-5 hover:-translate-y-1 hover:shadow-md transition-all duration-200 group relative" data-testid={`resume-card-${r.id}`}>
                <div className="flex items-start justify-between">
                  <Link to={`/builder/${r.id}`} className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-lg bg-blue-50 text-blue-600 flex items-center justify-center">
                      <FileText className="w-5 h-5" />
                    </div>
                    <div>
                      <div className="font-display font-semibold text-slate-900 group-hover:text-blue-600 transition">{r.title}</div>
                      <div className="text-xs text-slate-500 mt-0.5">{TEMPLATE_LABELS[r.template] || "Default"} template</div>
                    </div>
                  </Link>
                  <DropdownMenu>
                    <DropdownMenuTrigger asChild>
                      <Button variant="ghost" size="icon" className="h-8 w-8" data-testid={`resume-menu-${r.id}`}>
                        <MoreVertical className="w-4 h-4" />
                      </Button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent align="end">
                      <DropdownMenuItem onClick={() => nav(`/builder/${r.id}`)}>
                        <Pencil className="w-4 h-4 mr-2" /> Edit
                      </DropdownMenuItem>
                      <DropdownMenuItem onClick={() => duplicate(r.id)} data-testid={`resume-duplicate-${r.id}`}>
                        <Copy className="w-4 h-4 mr-2" /> Duplicate
                      </DropdownMenuItem>
                      <DropdownMenuItem onClick={() => remove(r.id)} className="text-red-600 focus:text-red-600" data-testid={`resume-delete-${r.id}`}>
                        <Trash2 className="w-4 h-4 mr-2" /> Delete
                      </DropdownMenuItem>
                    </DropdownMenuContent>
                  </DropdownMenu>
                </div>
                <div className="mt-4 pt-4 border-t border-slate-100 text-xs text-slate-500 flex items-center justify-between">
                  <span>Updated {formatDate(r.updated_at)}</span>
                  <Link to={`/builder/${r.id}`} className="text-blue-600 font-medium hover:underline">Open →</Link>
                </div>
              </Card>
            ))}
          </div>
        )}

        {/* ---------- Cover Letters Section ---------- */}
        <div className="mt-14 flex items-end justify-between flex-wrap gap-4 mb-6">
          <div>
            <div className="flex items-center gap-2 text-xs font-mono-display text-blue-700 bg-blue-50 inline-flex px-2.5 py-1 rounded-md border border-blue-100" data-testid="dashboard-cl-badge">
              <Mail className="w-3 h-3" /> Cover letters
            </div>
            <h2 className="mt-3 font-display text-2xl lg:text-3xl font-bold tracking-tight">Your cover letters</h2>
            <p className="mt-1 text-slate-500 text-sm">Guided builder with AI assist that uses only what you write.</p>
          </div>
          <Dialog open={openCreateCL} onOpenChange={setOpenCreateCL}>
            <DialogTrigger asChild>
              <Button className="bg-blue-600 hover:bg-blue-700 h-10 px-4" data-testid="dashboard-new-cl-btn">
                <Plus className="w-4 h-4 mr-2" /> New cover letter
              </Button>
            </DialogTrigger>
            <DialogContent>
              <DialogHeader>
                <DialogTitle>Create a cover letter</DialogTitle>
                <DialogDescription>Give it a name and optionally start from one of your resumes so contact info auto-fills.</DialogDescription>
              </DialogHeader>
              <div className="space-y-4">
                <div>
                  <Label htmlFor="cl-title">Title</Label>
                  <Input id="cl-title" value={newCLTitle} onChange={(e) => setNewCLTitle(e.target.value)} placeholder="e.g. Acme — Senior Designer" data-testid="new-cl-title-input" />
                </div>
                <div>
                  <Label htmlFor="cl-source">Start from resume (optional)</Label>
                  <select
                    id="cl-source"
                    value={newCLSource}
                    onChange={(e) => setNewCLSource(e.target.value)}
                    className="mt-1.5 w-full h-10 rounded-md border border-slate-200 px-3 text-sm bg-white focus:outline-none focus:ring-2 focus:ring-slate-300"
                    data-testid="new-cl-source-select"
                  >
                    <option value="">No — start blank</option>
                    {(resumes || []).map((r) => (
                      <option key={r.id} value={r.id}>{r.title}</option>
                    ))}
                  </select>
                  <p className="text-xs text-slate-500 mt-1.5">We&apos;ll copy over name, email, phone, and location.</p>
                </div>
              </div>
              <DialogFooter>
                <Button variant="outline" onClick={() => setOpenCreateCL(false)}>Cancel</Button>
                <Button onClick={createCoverLetter} disabled={creatingCL} className="bg-blue-600 hover:bg-blue-700" data-testid="new-cl-create-btn">
                  {creatingCL ? <Loader2 className="w-4 h-4 animate-spin" /> : "Create cover letter"}
                </Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>
        </div>

        {coverLetters === null ? (
          <div className="flex items-center justify-center py-10">
            <Loader2 className="w-5 h-5 animate-spin text-slate-400" />
          </div>
        ) : coverLetters.length === 0 ? (
          <Card className="p-8 text-center border-dashed" data-testid="cl-empty-state">
            <div className="w-12 h-12 rounded-xl bg-blue-50 text-blue-600 mx-auto flex items-center justify-center">
              <Mail className="w-6 h-6" />
            </div>
            <h3 className="mt-4 font-display text-lg font-semibold">No cover letters yet</h3>
            <p className="text-sm text-slate-500 mt-1 max-w-sm mx-auto">Pair a focused cover letter with your resume — it makes a measurable difference.</p>
            <Button onClick={() => setOpenCreateCL(true)} className="mt-5 bg-blue-600 hover:bg-blue-700">
              <Plus className="w-4 h-4 mr-2" /> Create cover letter
            </Button>
          </Card>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5" data-testid="cl-grid">
            {coverLetters.map((c) => (
              <Card key={c.id} className="p-5 hover:-translate-y-1 hover:shadow-md transition-all duration-200 group relative" data-testid={`cl-card-${c.id}`}>
                <div className="flex items-start justify-between">
                  <Link to={`/cover-letter/${c.id}`} className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-lg bg-blue-50 text-blue-600 flex items-center justify-center">
                      <Mail className="w-5 h-5" />
                    </div>
                    <div>
                      <div className="font-display font-semibold text-slate-900 group-hover:text-blue-600 transition">{c.title}</div>
                      <div className="text-xs text-slate-500 mt-0.5">Cover letter</div>
                    </div>
                  </Link>
                  <DropdownMenu>
                    <DropdownMenuTrigger asChild>
                      <Button variant="ghost" size="icon" className="h-8 w-8" data-testid={`cl-menu-${c.id}`}>
                        <MoreVertical className="w-4 h-4" />
                      </Button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent align="end">
                      <DropdownMenuItem onClick={() => nav(`/cover-letter/${c.id}`)}>
                        <Pencil className="w-4 h-4 mr-2" /> Edit
                      </DropdownMenuItem>
                      <DropdownMenuItem onClick={() => removeCoverLetter(c.id)} className="text-red-600 focus:text-red-600" data-testid={`cl-delete-${c.id}`}>
                        <Trash2 className="w-4 h-4 mr-2" /> Delete
                      </DropdownMenuItem>
                    </DropdownMenuContent>
                  </DropdownMenu>
                </div>
                <div className="mt-4 pt-4 border-t border-slate-100 text-xs text-slate-500 flex items-center justify-between">
                  <span>Updated {formatDate(c.updated_at)}</span>
                  <Link to={`/cover-letter/${c.id}`} className="text-blue-600 font-medium hover:underline">Open →</Link>
                </div>
              </Card>
            ))}
          </div>
        )}

        <DangerZone />
      </main>
    </div>
  );
}
